/**
 * room-finish.ts — which room finishes are ROLL carpet, and which are carpet TILES.
 *
 * Jacky, 18 October 2026: *"build a carpet tile category, so it doesnt run carpet roll"*.
 *
 * "Carpet" on a canvas room means BROADLOOM: a roll, a physical cut review, a carpet_layout, whole
 * lineal metres at a full roll width. "Carpet Tile" (the catalogue's own sub_type string) is laid and
 * priced per m², like any other tiled floor, and none of the roll machinery applies to it.
 *
 * The trap this file closes: every roll-carpet guard compared the room's label with `=== 'carpet'`.
 * Minto (P-1026-10296) had carpet tiles labelled "Carpet", so the Proposal release was blocked by a
 * roll-carpet cut-plan requirement that could never be met; the canvas was relabelled "Carpet Tile"
 * (save 8) the same day, which passed only because the comparison happened to be exact. These
 * predicates make that exactness the rule, and the tests hold it.
 */
export const CARPET_FINISH = 'Carpet';
export const CARPET_TILE_FINISH = 'Carpet Tile';
const norm = (f) => (f ?? '').trim().toLowerCase();
/** Broadloom roll carpet — EXACTLY "Carpet", case-insensitive. Never "Carpet Tile". */
export function isRollCarpetFinish(f) {
    return norm(f) === 'carpet';
}
/** Carpet tiles, however the canvas spelled them ("Carpet Tile", "carpet tiles", "CarpetTile"). */
export function isCarpetTileFinish(f) {
    return ['carpet tile', 'carpet tiles', 'carpettile', 'carpettiles'].includes(norm(f));
}
/**
 * Either kind of carpet — for the few places where the soft-floor family matters and the roll does not
 * (which stair install product prices a carpeted flight). Never use this for a cut plan or a roll order.
 */
export function isCarpetFamilyFinish(f) {
    return isRollCarpetFinish(f) || isCarpetTileFinish(f);
}
/** Does an in-scope room need ROLL carpet? This is the question the cut-review release guard asks. */
export function hasRollCarpetRoom(rooms) {
    return (rooms ?? []).some((r) => isRollCarpetFinish(r.flooring) && !r.out_of_scope && !r.is_void);
}
/**
 * THE SAME RULE FOR A CATALOGUE PRODUCT (18 October 2026). The catalogue files carpet tiles under
 * `type = 'Carpet'` with `sub_type = 'Carpet Tile'`, so the product TYPE alone never means "roll". Open-book
 * release read `/carpet/i` on the type and gave tiles a roll width (the tile's width), the carpet underlay
 * and the bedrooms-carpet preset. Precedent: `build-proposal-from-quote.ts` already asked
 * `type === 'Carpet' && sub_type !== 'Carpet Tile'`; these two predicates are that question, once.
 */
export function isCarpetTileProduct(type, subType) {
    return isCarpetTileFinish(subType) || isCarpetTileFinish(type);
}
/** Broadloom: catalogue type exactly "Carpet" and not a carpet tile. Roll width, roll run, carpet underlay. */
export function isRollCarpetProduct(type, subType) {
    return isRollCarpetFinish(type) && !isCarpetTileProduct(type, subType);
}
//# sourceMappingURL=room-finish.js.map